/**
 * 週報匯出：**打成檔 ＋ 下載**（P1-12 頁 14）
 *
 * 紅線
 *   · ⭐ `rules §七 F2`：Excel／CSV 一律經 `@coeldery/table-io`（**唔准**各頁自己砌 XML／CSV 字串）
 *   · 標籤一律由 `report-sheets.ts` 嘅 `ReportLabels` 傳入（頁面用 `t()`）→ 匯出檔跟當前語言
 *   · ⚠️ 匯出係**前端**做（`decisions/014`）：Workers bundle 唔塞 xlsx 邏輯，
 *     而且表頭文字住喺前端 i18n 字典（單一來源），唔使喺 Functions 再抄一份
 *
 * ⚠️ `reportFile()` 係純函式（Node 都跑得，測試咬佢）；只有 `downloadReport()` 掂 DOM。
 * ⚠️ 呢個檔用**相對路徑** import 模組（唔用 `@coeldery/*` alias）：`scripts/test-reports.mjs` 要喺
 *    Node 直接 import 呢個檔（alias 只喺 Vite／tsc 生效，Node 解唔到）→ 驗收先做得到機器檢查。
 */
import { csvBuild, xlsxBuild } from '../../packages/table-io/index.ts';
import type { WeeklyReport } from './api.ts';
import { reportCsvRows, reportSheets, type Cell, type ReportLabels } from './report-sheets.ts';

export type ReportFormat = 'xlsx' | 'csv';

export interface ReportFile {
  bytes: Uint8Array;
  mime: string;
  filename: string;
}

/** 檔案 MIME（Excel 靠佢開；CSV 帶 charset 免得中文亂碼） */
const MIME: Record<ReportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv;charset=utf-8',
};

/** 檔名一律用 `weekStart`（週報按週命名；ASCII → 唔會喺任何系統亂碼） */
export const reportFilename = (weekStart: string, format: ReportFormat): string =>
  `sms-weekly-${weekStart}.${format}`;

/** ⭐ 打成檔（純函式；唔掂 DOM） */
export function reportFile(
  report: WeeklyReport,
  labels: ReportLabels,
  format: ReportFormat,
  pendingRows: ReadonlyArray<readonly [string, string | number]> = [],
): ReportFile {
  const bytes =
    format === 'csv'
      ? csvBuild(reportCsvRows(report, labels, pendingRows))
      : xlsxBuild(reportSheets(report, labels, pendingRows));
  return { bytes, mime: MIME[format], filename: reportFilename(report.weekStart, format) };
}

/** 觸發瀏覽器下載（唯一掂 DOM 嘅地方；測試唔會行到） */
export function downloadReport(file: ReportFile): void {
  // ⚠️ 一定要 copy 落一個**自己嘅 `ArrayBuffer`**：`Uint8Array<ArrayBufferLike>` 唔可以直接做
  //    `BlobPart`（TS 會咬 `SharedArrayBuffer` 唔係 `ArrayBuffer`）
  const buf = new ArrayBuffer(file.bytes.byteLength);
  new Uint8Array(buf).set(file.bytes);
  const blob = new Blob([buf], { type: file.mime });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename;
    a.click();
  } finally {
    // 即刻放走 blob（延後一輪先 revoke，免得 Safari 未開始下載就斷）
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** 給測試／除錯：CSV 內容轉字串（`csvBuild` 有 UTF-8 BOM；呢度照留） */
export const csvText = (rows: readonly Cell[][]): string => new TextDecoder().decode(csvBuild(rows));
